import { ParamAttachmentId, ParamEventId, ParamOrganizationSlug, ParamProjectSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { formatToolCallInstruction } from "./tool-call-formatting-ALc98KqF.js";
import { blobToBase64 } from "./blob-utils-CdbE_jsd.js";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-event-attachment.ts
var get_event_attachment_default = defineTool({
	name: "get_event_attachment",
	skills: ["inspect"],
	requiredScopes: ["event:read"],
	description: [
		"Download attachments from a Sentry event.",
		"",
		"Use this tool when you need to:",
		"- Download files attached to a specific event",
		"- Access screenshots, log files, or other attachments uploaded with an error report",
		"- Retrieve attachment metadata and download URLs",
		"",
		"<examples>",
		"### Download a specific attachment by ID",
		"",
		"```",
		"get_event_attachment(organizationSlug='my-organization', projectSlug='my-project', eventId='c49541c747cb4d8aa3efb70ca5aba243', attachmentId='12345')",
		"```",
		"",
		"### List all attachments for an event",
		"",
		"```",
		"get_event_attachment(organizationSlug='my-organization', projectSlug='my-project', eventId='c49541c747cb4d8aa3efb70ca5aba243')",
		"```",
		"",
		"</examples>",
		"",
		"<hints>",
		"- If `attachmentId` is provided, the specific attachment will be downloaded as an embedded resource",
		"- If `attachmentId` is omitted, all attachments for the event will be listed with download information",
		"- The `projectSlug` is required to identify which project the event belongs to",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		projectSlug: ParamProjectSlug,
		eventId: ParamEventId,
		attachmentId: ParamAttachmentId.nullable().default(null),
		regionUrl: ParamRegionUrl.nullable().default(null)
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		setTag("organization.slug", params.organizationSlug);
		if (params.attachmentId) {
			const attachment = await apiService.getEventAttachment({
				organizationSlug: params.organizationSlug,
				projectSlug: params.projectSlug,
				eventId: params.eventId,
				attachmentId: params.attachmentId
			});
			const contentParts = [];
			const effectiveMimeType = attachment.contentType;
			const isBinary = !effectiveMimeType.startsWith("text/");
			if (isBinary) {
				const isImage = effectiveMimeType.startsWith("image/");
				const base64 = await blobToBase64(attachment.blob);
				if (isImage) {
					const image = {
						type: "image",
						mimeType: effectiveMimeType,
						data: base64
					};
					contentParts.push(image);
				} else {
					const resource = {
						type: "resource",
						resource: {
							uri: `file://${attachment.filename}`,
							mimeType: effectiveMimeType,
							blob: base64
						}
					};
					contentParts.push(resource);
				}
			}
			let output$1 = `# Event Attachment Download\n\n`;
			output$1 += `**Event ID:** ${params.eventId}\n`;
			output$1 += `**Attachment ID:** ${params.attachmentId}\n`;
			output$1 += `**Filename:** ${attachment.filename}\n`;
			output$1 += `**Type:** ${attachment.attachment.type}\n`;
			output$1 += `**Size:** ${attachment.attachment.size} bytes\n`;
			output$1 += `**MIME Type:** ${effectiveMimeType}\n`;
			output$1 += `**Created:** ${attachment.attachment.dateCreated}\n\n`;
			output$1 += `**Download URL:** ${attachment.downloadUrl}\n\n`;
			if (isBinary) {
				output$1 += `## Binary Content\n\n`;
				output$1 += `The attachment is included as a resource and accessible through your client.\n`;
			} else {
				const textContent = await attachment.blob.text();
				output$1 += `## File Content\n\n`;
				output$1 += `\`\`\`\n${textContent}\n\`\`\`\n\n`;
			}
			const text = {
				type: "text",
				text: output$1
			};
			contentParts.push(text);
			return contentParts;
		}
		const attachments = await apiService.listEventAttachments({
			organizationSlug: params.organizationSlug,
			projectSlug: params.projectSlug,
			eventId: params.eventId
		});
		let output = `# Event Attachments\n\n`;
		output += `**Event ID:** ${params.eventId}\n`;
		output += `**Project:** ${params.projectSlug}\n\n`;
		if (attachments.length === 0) {
			output += "No attachments found for this event.\n";
			return output;
		}
		output += `Found ${attachments.length} attachment(s):\n\n`;
		attachments.forEach((attachment, index) => {
			output += `## Attachment ${index + 1}\n\n`;
			output += `**ID:** ${attachment.id}\n`;
			output += `**Name:** ${attachment.name}\n`;
			output += `**Type:** ${attachment.type}\n`;
			output += `**Size:** ${attachment.size} bytes\n`;
			output += `**MIME Type:** ${attachment.mimetype}\n`;
			output += `**Created:** ${attachment.dateCreated}\n\n`;
			output += "To download this attachment with the attachmentId provided:\n";
			output += `${formatToolCallInstruction({
				toolName: "get_event_attachment",
				arguments: {
					organizationSlug: params.organizationSlug,
					projectSlug: params.projectSlug,
					eventId: params.eventId,
					attachmentId: attachment.id
				},
				experimentalMode: context.experimentalMode ?? false,
				availableToolNames: context.availableToolNames,
				directToolNames: context.directToolNames
			})}\n\n`;
		});
		return output;
	}
});

//#endregion
export { get_event_attachment_default };
//# sourceMappingURL=get-event-attachment-h8oTTPZt.js.map